import { cloneElement, isValidElement, useContext } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Copy from 'react-feather/dist/icons/copy';
import X from 'react-feather/dist/icons/x';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { copyAsCSV } from '../../../utils/utils';
import { CONSTANTS } from '../../../constants/constants';
import { GridContext } from '../../NRReactGrid/NRReactGridContext';

const { ITEM_SELECTED, ITEMS_SELECTED, TOOLTIP_COPY_CSV, TOOLTIP_UNSELECT } = CONSTANTS.TABLE.COPY_TO_CLIPBOARD_BANNER;

/**
 * Slim, decoupled successor to CopyToClipboardBanner. Owns the shared selection
 * chrome (count, copy-to-CSV, cancel) and injects `selectedRows` / `clearSelection`
 * into each table-specific action passed via `actions`.
 */
function NRTableBulkActions({ selectedItems = [], setSelection, rows = [], actions = [] }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { setShouldClearSelection } = useContext(GridContext);

  const selectedRows = selectedItems.map(item => rows[item]);

  const clearSelection = () => {
    setSelection([]);
    setShouldClearSelection(true);
  };

  const handleCopyCsvClick = async () => {
    try {
      await copyAsCSV(selectedRows);
      enqueueSnackbar(CONSTANTS.COPIED_TO_CLIPBOARD_MSG, { variant: 'success' });
      setShouldClearSelection(true);
    } catch (err) {
      enqueueSnackbar('Failed to copy CSV to clipboard.', { variant: 'error' });
    }
  };

  return (
    <Box
      sx={{
        height: 40,
        background: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(0.5, 2)
      }}
    >
      <Typography variant="body2" color="inherit">
        {selectedItems.length + ' '}
        {selectedItems.length > 1 ? ITEMS_SELECTED : ITEM_SELECTED}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {actions.map((action, index) =>
          isValidElement(action) ? cloneElement(action, { key: action.key ?? `bulk-action-${index}`, selectedRows, clearSelection }) : action
        )}
        <Tooltip title={TOOLTIP_COPY_CSV}>
          <IconButton aria-label="copy to csv" component="span" color="inherit" size="small" onClick={handleCopyCsvClick} sx={{ color: 'inherit' }}>
            <Copy color="currentColor" size={20} />
            <Typography variant={'subtitle2'} color="inherit" sx={{ ml: 1, mr: 1 }} children={TOOLTIP_COPY_CSV} />
          </IconButton>
        </Tooltip>
        <Tooltip title={TOOLTIP_UNSELECT}>
          <IconButton aria-label="unselect" component="span" color="inherit" size="small" onClick={clearSelection} sx={{ color: 'inherit' }}>
            <X color="currentColor" size={20} />
            <Typography variant={'subtitle2'} color="inherit" sx={{ ml: 1 }}>
              Cancel
            </Typography>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default NRTableBulkActions;
